import type {
  Product,
  ProductVariationAttribute,
  ProductVariationSelectorConfig,
} from './types'

const STOREFRONT_BOX_VARIANT_ID_SEPARATOR = '__cx__'
const STOREFRONT_BOX_SELECTOR_KEY = 'embalagem'
const STOREFRONT_BOX_SELECTOR_LABEL = 'Embalagem'

const normalizePositiveIntegerValue = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return Math.trunc(value)
  }

  if (typeof value !== 'string') return undefined

  const trimmed = value.trim()
  if (!/^\d+$/.test(trimmed)) return undefined

  const parsed = Number.parseInt(trimmed, 10)
  if (!Number.isFinite(parsed) || parsed <= 0) return undefined

  return Math.trunc(parsed)
}

const roundStorefrontPrice = (value: number, decimals = 2): number => {
  if (!Number.isFinite(value)) return 0
  return Number(value.toFixed(decimals))
}

const formatStorefrontCurrencyValue = (value: number): string => {
  if (!Number.isFinite(value)) return 'R$ 0,00'

  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

const buildStorefrontBoxVariantId = (baseProductId: string, quantity: number): string => (
  `${baseProductId}${STOREFRONT_BOX_VARIANT_ID_SEPARATOR}${Math.max(1, Math.trunc(quantity))}`
)

const parseStorefrontBoxVariantId = (
  value: string | null | undefined
): { baseProductId: string; quantity: number } | null => {
  const normalized = String(value ?? '').trim()
  if (!normalized) return null

  const separatorIndex = normalized.indexOf(STOREFRONT_BOX_VARIANT_ID_SEPARATOR)
  if (separatorIndex <= 0) return null

  const baseProductId = normalized.slice(0, separatorIndex).trim()
  const quantity = normalizePositiveIntegerValue(
    normalized.slice(separatorIndex + STOREFRONT_BOX_VARIANT_ID_SEPARATOR.length)
  )

  if (!baseProductId || !quantity) return null

  return { baseProductId, quantity }
}

const buildStorefrontBoxSavingsPercent = (
  compareAtPrice: number | undefined,
  actualPrice: number | undefined
): number | undefined => {
  if (
    typeof compareAtPrice !== 'number'
    || !Number.isFinite(compareAtPrice)
    || compareAtPrice <= 0
    || typeof actualPrice !== 'number'
    || !Number.isFinite(actualPrice)
    || actualPrice <= 0
    || compareAtPrice <= actualPrice
  ) {
    return undefined
  }

  return roundStorefrontPrice(((compareAtPrice - actualPrice) / compareAtPrice) * 100, 2)
}

const buildStorefrontBoxLabel = (quantity: number): string => (
  `CAIXA C/ ${Math.max(1, Math.trunc(quantity))}`
)

const appendStorefrontPackagingSelectorConfig = (
  configs: ProductVariationSelectorConfig[] | undefined
): ProductVariationSelectorConfig[] => {
  const current = Array.isArray(configs) ? [...configs] : []
  const alreadyConfigured = current.some((config) => config.key === STOREFRONT_BOX_SELECTOR_KEY)

  if (!alreadyConfigured) {
    current.push({
      key: STOREFRONT_BOX_SELECTOR_KEY,
      name: STOREFRONT_BOX_SELECTOR_LABEL,
      order: current.length + 1,
      selectorType: 'caixa',
    })
  }

  return current
}

const appendStorefrontPackagingVariationAttributes = (
  attributes: ProductVariationAttribute[] | undefined,
  packagingLabel: string,
  savingsText?: string,
): ProductVariationAttribute[] => {
  const current = Array.isArray(attributes) ? [...attributes] : []
  current.push({
    variationId: STOREFRONT_BOX_SELECTOR_KEY,
    name: STOREFRONT_BOX_SELECTOR_LABEL,
    value: packagingLabel,
    selectable: true,
    selectorType: 'caixa',
  })

  if (savingsText) {
    current.push({
      name: 'Oferta',
      value: savingsText,
    })
  }

  return current
}

const buildStorefrontBoxSavingsText = (
  product: Pick<Product, 'boxSavingsAmount' | 'boxSavingsPercent' | 'packagingLabel' | 'price'>
): string | undefined => {
  const parts: string[] = []

  if (
    typeof product.boxSavingsPercent === 'number'
    && Number.isFinite(product.boxSavingsPercent)
    && product.boxSavingsPercent > 0
  ) {
    parts.push(`${product.boxSavingsPercent.toFixed(2).replace('.', ',')}% OFF`)
  }

  if (
    typeof product.boxSavingsAmount === 'number'
    && Number.isFinite(product.boxSavingsAmount)
    && product.boxSavingsAmount > 0
  ) {
    parts.push(`Economia de ${formatStorefrontCurrencyValue(product.boxSavingsAmount)} por caixa`)
  }

  if (typeof product.price === 'number' && Number.isFinite(product.price) && product.price > 0) {
    parts.push(`Valor da caixa fechada ${formatStorefrontCurrencyValue(product.price)}`)
  }

  return parts.length > 0 ? parts.join(' - ') : undefined
}

export function resolveStorefrontCartBaseProductId(
  product: Pick<Product, 'id' | 'cartBaseProductId'> | string
): string {
  if (typeof product === 'string') {
    const synthetic = parseStorefrontBoxVariantId(product)
    return synthetic?.baseProductId ?? product.trim()
  }

  const explicitBaseId = String(product.cartBaseProductId ?? '').trim()
  if (explicitBaseId) return explicitBaseId

  const synthetic = parseStorefrontBoxVariantId(product.id)
  return synthetic?.baseProductId ?? String(product.id ?? '').trim()
}

export function resolveStorefrontCartBoxQuantity(
  product: Pick<Product, 'id' | 'packagingQuantity'> | string
): number {
  if (typeof product !== 'string') {
    const explicitQuantity = normalizePositiveIntegerValue(product.packagingQuantity)
    if (explicitQuantity) return explicitQuantity
  }

  const value = typeof product === 'string' ? product : product.id
  const synthetic = parseStorefrontBoxVariantId(value)
  return synthetic?.quantity ?? 1
}

export function buildStorefrontPurchaseOptionProduct(
  product: Product,
  qtdeCaixa = 1
): Product {
  const normalizedQuantity = Math.max(1, Math.trunc(qtdeCaixa))
  const baseProductId = resolveStorefrontCartBaseProductId(product)

  const baseVariationAttributes = Array.isArray(product.variationAttributes)
    ? [...product.variationAttributes].filter((attribute) => (
      String(attribute.variationId ?? '').trim() !== STOREFRONT_BOX_SELECTOR_KEY
      && String(attribute.name ?? '').trim().toLowerCase() !== STOREFRONT_BOX_SELECTOR_LABEL.toLowerCase()
    ))
    : []

  const baseSelectorConfig = Array.isArray(product.variationSelectorConfig)
    ? product.variationSelectorConfig.filter((config) => config.key !== STOREFRONT_BOX_SELECTOR_KEY)
    : []

  if (normalizedQuantity <= 1) {
    const unitLabel = 'UNITARIO'
    return {
      ...product,
      id: baseProductId,
      cartBaseProductId: baseProductId,
      packagingMode: 'unit',
      packagingQuantity: 1,
      packagingLabel: unitLabel,
      unitPrice: typeof product.unitPrice === 'number' ? product.unitPrice : product.price,
      variationAttributes: appendStorefrontPackagingVariationAttributes(
        baseVariationAttributes,
        unitLabel,
      ),
      variationSelectorConfig: appendStorefrontPackagingSelectorConfig(baseSelectorConfig),
      navigationTargetProductId: product.navigationTargetProductId ?? baseProductId,
      hasVariations: false,
    }
  }

  const matchedBoxOption = (product.boxOptions ?? []).find((option) => option.quantity === normalizedQuantity)
  const packagingLabel = matchedBoxOption?.label ?? buildStorefrontBoxLabel(normalizedQuantity)
  const unitPrice = matchedBoxOption?.unitPrice
    ?? (typeof product.unitPrice === 'number' ? product.unitPrice : product.price)
  const totalPrice = matchedBoxOption?.totalPrice
    ?? (typeof unitPrice === 'number' ? roundStorefrontPrice(unitPrice * normalizedQuantity, 4) : product.price)
  const originalTotalPrice = matchedBoxOption?.originalTotalPrice
    ?? (
      typeof matchedBoxOption?.originalUnitPrice === 'number'
        ? roundStorefrontPrice(matchedBoxOption.originalUnitPrice * normalizedQuantity, 4)
        : typeof product.originalPrice === 'number'
          ? roundStorefrontPrice(product.originalPrice * normalizedQuantity, 4)
          : undefined
    )
  const boxSavingsAmount = matchedBoxOption?.savingsAmount
    ?? (
      typeof originalTotalPrice === 'number' && typeof totalPrice === 'number'
        ? roundStorefrontPrice(Math.max(originalTotalPrice - totalPrice, 0), 4)
        : undefined
    )
  const boxSavingsPercent = matchedBoxOption?.savingsPercent
    ?? buildStorefrontBoxSavingsPercent(originalTotalPrice, totalPrice)

  return {
    ...product,
    id: buildStorefrontBoxVariantId(baseProductId, normalizedQuantity),
    name: `${product.baseName || product.name} - ${packagingLabel}`,
    cartBaseProductId: baseProductId,
    packagingMode: 'box',
    packagingQuantity: normalizedQuantity,
    packagingLabel,
    unitPrice,
    boxSavingsAmount,
    boxSavingsPercent,
    variationAttributes: appendStorefrontPackagingVariationAttributes(
      baseVariationAttributes,
      packagingLabel,
      buildStorefrontBoxSavingsText({
        packagingLabel,
        boxSavingsAmount,
        boxSavingsPercent,
        price: totalPrice,
      }),
    ),
    variationSelectorConfig: appendStorefrontPackagingSelectorConfig(baseSelectorConfig),
    navigationTargetProductId: product.navigationTargetProductId ?? baseProductId,
    price: totalPrice,
    originalPrice: originalTotalPrice ?? totalPrice,
    discountedPrice: totalPrice,
    // Box variants only have the resolved box price in the current payload.
    // Reusing unit-level alternative prices here mixes contexts on the UI.
    cnpjIePrice: undefined,
    distributionPrice: undefined,
    installmentValue: undefined,
    hasVariations: false,
  }
}
